import { ListDataSourcesResponse } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { useQueries, UseQueryOptions } from 'react-query';
import { http, HttpError } from '@frontend/fetch';
import { useQuery } from '@frontend/react-query-helpers';
import { SchemaIntegrationsService } from '@frontend/schema';
import { documentCenterSupportedPMS, supportedPMSForInsuranceWriteback } from './config';
import { ClickToOpenSupportedHookResponse, WritebackSupportedHookResponse } from './types';

const queryKeys = {
  dataSourceDetails: (locationId: string, dataSourceId?: string) => [
    locationId,
    'integrations',
    dataSourceId,
    'datasource-details',
  ],
  dataSources: (locationId: string) => ['integrations', 'data-sources', locationId],
  syncAppInfo: (sourceId: string) => ['integrations', 'sync-app-info', sourceId],
};

interface GetDataSourceDetailsParams {
  locationId: string;
  sourceId?: string;
  disabled?: boolean;
}

export const useGetDataSourceDetails = ({ locationId, sourceId, disabled }: GetDataSourceDetailsParams) => {
  return useQuery(
    queryKeys.dataSourceDetails(locationId, sourceId ?? ''),
    () => SchemaIntegrationsService.GetDataSource({ locationId, sourceId: sourceId }),
    {
      cacheTime: 8 * 3.6e6, // Adding stale time as 8 hr since the data is expected to be static for the entire day
      staleTime: 8 * 3.6e6, // Adding cache time as 8 hr since the data is expected to be static for the entire day
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: Boolean(locationId && sourceId && !disabled),
    }
  );
};

export const useIsWritebackSupported = ({
  locationId,
  sourceId,
  disabled,
}: GetDataSourceDetailsParams): WritebackSupportedHookResponse => {
  const { data, isLoading } = useGetDataSourceDetails({ locationId, sourceId, disabled });

  if (!sourceId || isLoading) {
    return {
      isLoading,
      PMSName: '',
      isInsuranceDetailsWritebackSupported: false,
      isDocumentCenterWritebackSupported: false,
    };
  }

  const PMSName = data?.integration?.displayName ?? '';
  const comparePMSName = PMSName.toLowerCase().replace(/\s/g, '') ?? '';

  const hasInsuranceDetailsSupportedPMS =
    !!comparePMSName && supportedPMSForInsuranceWriteback.some((pms) => comparePMSName.includes(pms));

  const hasDocumentCenterSupportedPMS =
    !!comparePMSName && documentCenterSupportedPMS.some((pms) => comparePMSName.includes(pms));

  return {
    isInsuranceDetailsWritebackSupported: hasInsuranceDetailsSupportedPMS,
    isDocumentCenterWritebackSupported: hasDocumentCenterSupportedPMS,
    PMSName,
    isLoading,
  };
};

export const useIsClickToOpenSupported = ({
  locationId,
  sourceId,
}: GetDataSourceDetailsParams): ClickToOpenSupportedHookResponse => {
  const { data, isLoading } = useGetDataSourceDetails({ locationId, sourceId });

  if (!sourceId || isLoading) {
    return {
      isLoading,
      pmsName: '',
      isClickToOpenSupported: false,
    };
  }

  const PMSName = data?.integration?.displayName ?? '';
  const comparePMSName = PMSName.toLowerCase().replace(/\s/g, '') ?? '';

  return {
    isClickToOpenSupported: !!comparePMSName && comparePMSName === 'dentrixg5',
    pmsName: comparePMSName,
    isLoading,
  };
};

export const useGetMultiDataSources = <T = ListDataSourcesResponse>(
  request: { locationIds: string[] },
  options?: UseQueryOptions<ListDataSourcesResponse, HttpError, T>
) => {
  return useQueries(
    request.locationIds.map((locationId) => ({
      queryKey: queryKeys.dataSources(locationId),
      queryFn: async () => await SchemaIntegrationsService.ListDataSourcesByLocationID({ locationId }),
      enabled: !!locationId,
      ...options,
    }))
  );
};

type SyncAppInfo = {
  name: string;
  version: string;
};

export const useGetMultiSyncAppInfo = <T = SyncAppInfo>(
  request: { sourceIds: string[] },
  options?: UseQueryOptions<SyncAppInfo, HttpError, T>
) => {
  return useQueries(
    request.sourceIds.map((sourceId) => ({
      queryKey: queryKeys.syncAppInfo(sourceId),
      queryFn: async () => {
        const syncAppInfo = await http
          .post<{ data: { Response: string } }>('/support/v1/syncapp/executerpc', {
            rpcMethod: 'RPCAdapter.IntegrationInformation',
            rpcParams: [],
            sourceId,
          })
          .then((response) => {
            try {
              return JSON.parse(atob(response.data.Response)) as SyncAppInfo;
            } catch (error) {
              throw new Error('Invalid response format');
            }
          });

        return syncAppInfo;
      },
      enabled: !!sourceId,
      ...options,
    }))
  );
};
